import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Phone, Mail, ExternalLink, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import SEO from '../components/SEO';

const MedicareExplained = () => {
  return (
    <div className="medicare-explained-page overflow-x-hidden">
      <SEO 
        title="Medicare Explained"
        description="Understanding Medicare Parts A, B, C, and D. Complete guide to Original Medicare, Medicare Advantage, and Medicare Supplement Insurance. Expert help navigating your Medicare options."
        keywords="Medicare explained, Medicare Part A, Medicare Part B, Medicare Part C, Medicare Part D, Medicare Advantage, Medigap, Medicare Supplement, Original Medicare"
        path="/medicare-explained"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-teal-50 to-blue-50 py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="bg-white rounded-lg shadow-xl p-4 flex items-center justify-center">
              <img 
                src="/images/fraud-2.png"
                alt="Medicare Card"
                className="w-full max-w-md object-contain rounded-lg"
              />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">Medicare Explained</h1>
              <p className="text-2xl font-semibold text-gray-800 mb-4">
                Original Medicare = Part A and Part B
              </p>
              <p className="text-xl text-gray-700 mb-8">
                We are here to help with Medicare A, B, C and D
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Important Message */}
      <section className="py-8 px-4 bg-blue-50">
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-2xl font-bold text-gray-900">
            "As our client, we are available all year to guide, advocate, and answer any questions."
          </p>
        </div>
      </section>

      {/* 4 Medicare Parts Overview - Narrow Section */}
      <section className="py-8 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-2 border-red-400 bg-red-50">
              <CardContent className="p-4 text-center">
                <h3 className="text-xl font-bold text-red-700 mb-1">Part A</h3>
                <p className="text-sm font-semibold text-gray-900 mb-1">Hospital Insurance</p>
                <p className="text-sm text-gray-700 mb-1">A for "Admitted"</p>
                <p className="text-sm text-gray-700">Covers Inpatient Care</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-gray-400 bg-white">
              <CardContent className="p-4 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-1">Part B</h3>
                <p className="text-sm font-semibold text-gray-900 mb-1">Outpatient Care</p>
                <p className="text-sm text-gray-700 mb-1">B for "Biopsy"</p>
                <p className="text-sm text-gray-700">Covers 80% of outpatient care</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-yellow-400 bg-yellow-50">
              <CardContent className="p-4 text-center">
                <h3 className="text-xl font-bold text-yellow-700 mb-1">Part D</h3>
                <p className="text-sm font-semibold text-gray-900 mb-1">Drug Coverage</p>
                <p className="text-sm text-gray-700 mb-1">D for "Rx Drugs"</p>
                <p className="text-sm text-gray-700">Covers Prescription Drugs</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-blue-400 bg-blue-50">
              <CardContent className="p-4 text-center">
                <h3 className="text-xl font-bold text-blue-700 mb-1">Part C</h3>
                <p className="text-sm font-semibold text-gray-900 mb-1">Advantage Plan</p>
                <p className="text-sm text-gray-700 mb-1">C for "Combo"</p>
                <p className="text-sm text-gray-700">Medicare Advantage Plan</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Original Medicare */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <Card className="border-2 border-teal-200">
                <CardContent className="p-6">
                  <h4 className="text-2xl font-bold text-teal-600 mb-4">Part A</h4>
                  <p className="font-bold text-xl mb-3 text-gray-900">Cost: $0</p>
                  <p className="text-gray-800 font-semibold text-lg mb-4">Hospital / Inpatient Coverage</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 text-lg">
                    <li>Hospital stays</li>
                    <li>Skilled nursing facility care</li>
                    <li>Hospice care</li>
                    <li>Home health care</li>
                    <li className="font-bold text-red-600">$1,736 deductible</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-teal-200">
                <CardContent className="p-6">
                  <h4 className="text-2xl font-bold text-teal-600 mb-4">Part B</h4>
                  <div className="mb-3">
                    <p className="font-bold text-xl text-gray-900">
                      Cost: $202.90* premium + 20% coinsurance
                    </p>
                    <p className="text-sm text-gray-600 italic">
                      *Standard Medicare Part B Premium for 2026.{' '}
                      <a 
                        href="/pdfs/irmaa.pdf" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-teal-600 hover:text-teal-700 underline font-semibold"
                      >
                        See IRMAA chart
                      </a>
                    </p>
                  </div>
                  <p className="font-bold text-red-600 mb-3 text-lg">+ $283 deductible annually</p>
                  <p className="text-gray-800 font-semibold text-lg mb-4">Medical / Outpatient Coverage</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 text-lg">
                    <li>Doctor visits</li>
                    <li>Outpatient care</li>
                    <li>Preventive services</li>
                    <li>Medical equipment</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="max-w-3xl mx-auto bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-8">
              <h4 className="text-2xl font-bold text-red-700 mb-4">Original Medicare does not cover:</h4>
              <ul className="list-disc list-inside text-gray-800 space-y-2 text-lg">
                <li>Prescription Drugs</li>
                <li>Dental</li>
                <li>Vision</li>
                <li>Hearing</li>
                <li className="font-bold text-xl">It does not have a maximum out of pocket or annual stop loss either.</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-8 text-center">
              <p className="text-3xl font-bold text-gray-900 mb-3">THIS IS WHY IT IS SO IMPORTANT TO CONTACT OUR OFFICE:</p>
              <p className="text-2xl font-bold text-red-600 mb-3">"ORIGINAL MEDICARE ALONE COULD COST YOU THOUSANDS."</p>
              <p className="text-xl font-bold text-gray-800">"We will Show you options to Cover the 20% Gap in Coverage."</p>
            </div>
          </div>

          {/* Two Options Section */}
          <div className="mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-8 text-center">
              YOU HAVE 2 OPTIONS TO COVER YOUR 20% PART B RESPONSIBILITY
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Option 1 - Bold Blue Background */}
              <div className="bg-blue-600 rounded-lg p-8 border-4 border-blue-700 shadow-xl">
                <h4 className="text-2xl font-bold text-white mb-6">
                  Option 1: BUY A SUPPLEMENT PLAN TO PAY THE 20%
                </h4>
                <Card className="border-2 border-blue-400 bg-white">
                  <CardContent className="p-6">
                    <h5 className="text-xl font-bold mb-4 text-gray-900">Medicare Supplement (Medigap)</h5>
                    <ul className="list-disc list-inside text-gray-800 space-y-3 text-lg">
                      <li className="font-semibold">Covers Part A deductible</li>
                      <li className="font-semibold">Covers the 20% coinsurance</li>
                      <li className="font-semibold">Once you pay your $283 deductible, the plan pays the rest</li>
                      <li className="font-semibold">Nationwide Network</li>
                      <li className="font-semibold">Can see any doctor that accepts Medicare</li>
                      <li className="font-semibold">No pre-authorizations for most care</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              {/* Option 2 - Bold Red Background */}
              <div className="bg-red-600 rounded-lg p-8 border-4 border-red-700 shadow-xl">
                <h4 className="text-2xl font-bold text-white mb-6">
                  Option 2: REPLACE ORIGINAL MEDICARE with PART C: MEDICARE ADVANTAGE PLAN
                </h4>
                <Card className="border-2 border-teal-400">
                  <CardContent className="p-6">
                    <h5 className="text-xl font-bold mb-4 text-gray-900">All-in-One Coverage</h5>
                    <ul className="list-disc list-inside text-gray-800 space-y-3 text-lg">
                      <li className="font-semibold">Includes Part A and B benefits</li>
                      <li className="font-semibold">Removes Part A and B deductibles</li>
                      <li className="font-semibold">Copayments and Co-insurance for care</li>
                      <li className="font-semibold">Similar to Employer Coverage</li>
                      <li className="font-semibold">Often includes Part D (prescriptions)</li>
                      <li className="font-semibold">May include dental, vision, hearing</li>
                      <li className="font-semibold">There is a Maximum out of Pocket for the year</li>
                      <li className="font-semibold">Network-based (HMO, PPO)</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          {/* Prescription Drug Coverage */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h4 className="text-3xl font-bold text-teal-600 mb-6 text-center">Part D: Prescription Drug Coverage</h4>
            <Card className="border-2 border-teal-200">
              <CardContent className="p-6">
                <ul className="list-disc list-inside text-gray-800 space-y-3 text-lg">
                  <li className="font-semibold">Standalone prescription drug plan</li>
                  <li className="font-semibold">Works with Original Medicare or Supplement</li>
                  <li className="font-semibold">Helps cover the cost of medications</li>
                  <li className="font-semibold">Multiple plan options available</li>
                  <li className="font-semibold">$2,100 annual maximum out of pocket</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How Do I Sign Up - Three Steps Process */}
      <section className="py-12 px-4 bg-teal-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Get Your Personalized Plan Comparison- No Cost</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Step 1 */}
            <div className="bg-white rounded-lg p-8 shadow-md">
              <div className="text-6xl font-bold text-teal-600 mb-4">01</div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Take the Survey</h3>
              <p className="text-gray-700 text-lg mb-6">Tell us about your doctors, medications, and what matters most to you.</p>
              <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white text-lg py-6">
                <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                  Medicare Survey
                </a>
              </Button>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-lg p-8 shadow-md">
              <div className="text-6xl font-bold text-teal-600 mb-4">02</div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">We Research Your Options</h3>
              <p className="text-gray-700 text-lg">We analyze options across carriers and explain the tradeoffs clearly.</p>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-lg p-8 shadow-md">
              <div className="text-6xl font-bold text-teal-600 mb-4">03</div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Schedule Your Call</h3>
              <p className="text-gray-700 text-lg mb-6">We review your options together, answering any questions with a one on one meeting.</p>
              <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white text-lg py-6">
                <a href="https://calendly.com/llplans/30min" target="_blank" rel="noopener noreferrer">
                  My Calendar
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <ContactForm />
    </div>
  );
};

export default MedicareExplained;
